import type { PricingEngineType } from '@/shared/generated'

import { useMemo } from 'react'
import { map, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useSettingsSchemasQuery } from '@/entities/core'

import { RHFSelect } from '@/shared/components/HookForm'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { getResolvedSchema } from '@/shared/tools/json-schema-utils'

export function TargetPriceTypeController({
	engine,
	name,
}: {
	engine: PricingEngineType
	name: string
}) {
	const { t } = useTranslation()

	const priceTypeSettingsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema(`part:tactics_${PRICING_ENGINES[engine]}`),
			prop('properties.target_price_type'),
		]),
		refetchOnWindowFocus: 'always',
		enabled: !!engine,
	})

	const options = useMemo(
		() =>
			map(
				(item) => ({
					label: t(`price_type.${item}`, { defaultValue: item }),
					value: item,
				}),
				priceTypeSettingsSchema.data?.enum || []
			),
		[priceTypeSettingsSchema.data]
	)
	const defaultValue = priceTypeSettingsSchema?.data?.default

	return priceTypeSettingsSchema?.isLoading ? (
		<Skeleton className='h-8 w-52' />
	) : (
		<Card>
			<CardContent>
				<RHFSelect
					label={t('target_price_type.label', {
						defaultValue: 'Pricing approach',
					})}
					name={name}
					disabled={!options.length}
					defaultValue={defaultValue}
					options={options}
				/>
			</CardContent>
		</Card>
	)
}
const PRICING_ENGINES = {
	RB: 'rb',
	ML: 'ml',
	MD: 'md',
	Promo: 'promo',
}
